import cn from 'classnames';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { isDesktop } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getSkinId } from 'src/helpers/utils';
import { useI18n } from 'src/hooks/use-i18n-translation';
import { getRafflesBanners } from 'src/services/cmsApi';
import { getActiveRaffles, getActiveRafflesTickets, getPrizeStructures } from 'src/services/raffleDrawApi';
import { CmsRafflePageResponse } from 'src/types/cms/cmsPageTypes';
import { RootState } from 'src/types/store-types';
import RaffleDrawInfo from './components/RaffleDrawInfo';
import RaffleDrawRules from './components/RaffleDrawRules';
import RaffleDrawTickets from './components/RaffleDrawTickets';
import './styles.scss';
import {
  IActiveRafflesTicketsType,
  IActiveRafflesType,
  IPrizePoolItem,
  RaffleCmsContentItem,
  RaffleSection,
} from './types';

const RaffleDraw: FC = () => {
  const { user } = useSelector((state: RootState) => state.user);
  const { locale } = useI18n();
  const skinId = getSkinId();
  const { t } = useTranslation();

  const [raffles, setRaffles] = useState<IActiveRafflesType[] | null>(null);
  const [prizePool, setPrizePool] = useState<IPrizePoolItem[]>([]);
  const [raffleTickets, setRaffleTickets] = useState<IActiveRafflesTicketsType[] | null>(null);
  const [raffleCmsData, setRaffleCmsData] = useState<RaffleCmsContentItem[]>([]);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const lastPrizeStructIdRef = useRef<number | null>(null);

  const getActiveRafflesReq = async (): Promise<void> => {
    const res = await getActiveRaffles({ operatorId: skinId });
    if (res.success) setRaffles(res.result.raffles);
    else console.error(res);
  };

  const getPrizeStructuresReq = async (id: number): Promise<void> => {
    const res = await getPrizeStructures({ id });
    if (res.success) setPrizePool(res.result.prizeStructures);
    else console.error(res);
  };

  const getActiveRafflesTicketsReq = async (): Promise<void> => {
    const res = await getActiveRafflesTickets();
    if (res.success) setRaffleTickets(res.result);
    else console.error(res);
  };

  const getActiveRafflesBannersReq = async (): Promise<void> => {
    try {
      const res: CmsRafflePageResponse = await getRafflesBanners(locale);

      const raffleDrawContent = res.success && res.result?.[0]?.content?.pageFields?.raffleDrawContent;

      setRaffleCmsData(Array.isArray(raffleDrawContent) ? (raffleDrawContent as RaffleCmsContentItem[]) : []);
    } catch (error) {
      console.error(error);
      setRaffleCmsData([]);
    }
  };

  const onSelectTab = (tab: number): void => {
    setSelectedTab(tab);
  };

  useEffect(() => {
    void getActiveRafflesReq();
  }, []);

  useEffect(() => {
    void getActiveRafflesBannersReq();
  }, [locale]);

  useEffect(() => {
    if (!user) return;
    void getActiveRafflesTicketsReq();
  }, [user]);

  const orderedData = useMemo((): RaffleSection[] => {
    if (!raffles) return [];

    const activeRafflesMap = new Map((raffleTickets ?? []).map((r) => [r.id, r]));

    return raffles
      .map((r) => ({
        ...r,
        ...activeRafflesMap.get(r.id),
      }))
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
      .map((raffle) => ({
        category: raffle.category,
        raffles: [raffle],
        template_id: raffle.template_id,
        id: raffle.id,
      }));
  }, [raffles, raffleTickets]);

  useEffect(() => {
    if (!orderedData.length) {
      if (selectedTab !== 0) setSelectedTab(0);
      return;
    }

    if (orderedData.some((section) => section.id === selectedTab)) return;

    setSelectedTab(orderedData[0]?.id ?? 0);
  }, [orderedData, selectedTab]);

  const currentSection = useMemo(
    () => orderedData.find((section) => section.id === selectedTab),
    [orderedData, selectedTab]
  );
  const prizeStructId = useMemo(() => currentSection?.raffles?.[0]?.prize_struct_id, [currentSection]);

  const currentRaffleCmsData = useMemo(
    () => raffleCmsData.find((item) => Number(item.templateId) === currentSection?.template_id),
    [raffleCmsData, currentSection]
  );
  useEffect(() => {
    if (!prizeStructId) {
      lastPrizeStructIdRef.current = null;
      return;
    }
    if (lastPrizeStructIdRef.current === prizeStructId) return;
    lastPrizeStructIdRef.current = prizeStructId;
    void getPrizeStructuresReq(prizeStructId);
  }, [prizeStructId]);

  return (
    <div className={cn('raffle_draw_content_wrapper', { with_tabs: orderedData.length > 1 })} id="raffleDrawContainer">
      {orderedData.length > 0 && currentSection ? (
        <>
          <div className="raffle_draw_tabs_wrapper">
            <div className="raffle_draw_tabs">
              {orderedData.map((section) => {
                const firstRaffle = section.raffles[0];
                if (!firstRaffle) return null;

                return (
                  <div
                    key={section.id}
                    onClick={() => onSelectTab(section.id)}
                    className={cn('tab_item', { selected: selectedTab === section.id })}
                  >
                    <img src={firstRaffle.img_url} alt={firstRaffle.name} />
                    <p>
                      <span className="raffle_draw_name">{firstRaffle.name}</span>
                      <span className="raffle_draw_short_description">{firstRaffle.short_description}</span>
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="raffle_draw_content">
            {currentSection?.raffles?.map((raffle) => (
              <div key={raffle.id} className="raffle_draw_item">
                <div className="raffle_draw_banner">
                  <RaffleDrawInfo
                    expireTime={raffle?.expire_time}
                    slider={
                      (isDesktop ? currentRaffleCmsData?.sliderDesktop : currentRaffleCmsData?.sliderMobile) ?? []
                    }
                  />
                </div>
                <div className="raffle_draw_rule">
                  <RaffleDrawRules raffleRules={currentRaffleCmsData?.raffleRules} />
                </div>
                <div className="raffle_draw_tickets">
                  <RaffleDrawTickets prizePool={prizePool} raffleData={raffle} />
                </div>
              </div>
            ))}
            <div className="raffle_draw_content_faq">
              <p>{t('faq')}</p>
              <div dangerouslySetInnerHTML={{ __html: currentRaffleCmsData?.faq ?? '' }} />
            </div>
          </div>
        </>
      ) : (
        <p className="empty_state">{t('emptyRaffle')}</p>
      )}
    </div>
  );
};

export default RaffleDraw;
